import { Stack, Text } from '@mantine/core';
import { ReactNode } from 'react';
import { UnderlineTooltip } from 'src/UI/common';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Expenditure } from '../hooks/useExpenditures';
import { Income } from '../hooks/useIncomes';
import { LifeEvent } from '../hooks/useLifeEvents';
import {
  expenditureLeftOutReason,
  expenditureName,
  frequencyExplanation,
  INCOME_TOTAL_REASON_LABELS,
  incomeLeftOutReason,
  incomeName,
  leftOutNames,
  lifeEventLeftOutReason,
  lifeEventName,
  MISSING_AMOUNT_EXPLANATION,
} from '../leftOut';
import { NOT_RECORDED } from '../notRecorded';
import { calculateAnnualAmount, CashflowFrequency, Period, totalForPeriod } from '../utils';
import { LeftOutNote } from './LeftOutNote';
import { formatMoney, TenantCurrency } from 'src/common/currency';

function missingAmountCell(): ReactNode {
  return (
    <UnderlineTooltip label={MISSING_AMOUNT_EXPLANATION} c="gray.6">
      {NOT_RECORDED}
    </UnderlineTooltip>
  );
}

export function amountCell(amount: number | null | undefined, currency: TenantCurrency): ReactNode {
  return amount == null ? missingAmountCell() : formatMoney(amount, currency);
}

export function frequencyAmountCell(
  period: Period,
  recorded: number | null | undefined,
  frequency: CashflowFrequency | null | undefined,
  currency: TenantCurrency,
  options?: { positive?: boolean },
): ReactNode {
  if (recorded == null) return missingAmountCell();

  const prefix = options?.positive ? '+' : '';
  const annually = calculateAnnualAmount(recorded, frequency);

  if (annually === null) {
    return (
      <UnderlineTooltip label={frequencyExplanation(frequency)} c="gray.6">
        {prefix}
        {formatMoney(recorded, currency)}
      </UnderlineTooltip>
    );
  }

  const monthly = annually / 12;
  const displayed = period === 'monthly' ? monthly : annually;
  const label =
    period === 'monthly'
      ? `Annually: ${formatMoney(annually, currency)}`
      : `Monthly: ${formatMoney(monthly, currency)}`;

  return (
    <UnderlineTooltip label={label}>
      {prefix}
      {formatMoney(displayed, currency)}
    </UnderlineTooltip>
  );
}

function otherPeriod(period: Period): Period {
  return period === 'monthly' ? 'annually' : 'monthly';
}

function periodLabel(period: Period): string {
  return period === 'monthly' ? 'Monthly' : 'Annually';
}

export function IncomeSectionTotal<T extends Income>({
  incomes,
  period,
  currency,
  nameOf = incomeName,
}: {
  incomes: T[];
  period: Period;
  currency: TenantCurrency;
  nameOf?: (income: T) => string;
}) {
  const preTax = totalForPeriod(incomes, item => item.amountPreTax, period);
  const postTax = totalForPeriod(incomes, item => item.amountPostTax, period);
  const other = otherPeriod(period);
  const otherPreTax = totalForPeriod(incomes, item => item.amountPreTax, other);
  const otherPostTax = totalForPeriod(incomes, item => item.amountPostTax, other);
  const tooltipLabel = `${periodLabel(other)}: +${formatMoney(otherPreTax, currency)} (${formatMoney(otherPostTax, currency)} after tax)`;

  return (
    <Stack gap={2} align="flex-end">
      <SectionTotal tooltipLabel={tooltipLabel}>
        +{formatMoney(preTax, currency)} ({formatMoney(postTax, currency)} after tax)
      </SectionTotal>
      <LeftOutNote
        names={leftOutNames(incomes, incomeLeftOutReason, nameOf)}
        labels={INCOME_TOTAL_REASON_LABELS}
      />
    </Stack>
  );
}

export function ExpenditureSectionTotal<T extends Expenditure>({
  expenditures,
  period,
  currency,
  nameOf = expenditureName,
}: {
  expenditures: T[];
  period: Period;
  currency: TenantCurrency;
  nameOf?: (expenditure: T) => string;
}) {
  const total = totalForPeriod(expenditures, item => item.amount, period);
  const other = totalForPeriod(expenditures, item => item.amount, otherPeriod(period));
  const tooltipLabel = `${periodLabel(otherPeriod(period))}: -${formatMoney(other, currency)}`;

  return (
    <Stack gap={2} align="flex-end">
      <SectionTotal tooltipLabel={tooltipLabel}>-{formatMoney(total, currency)}</SectionTotal>
      <LeftOutNote names={leftOutNames(expenditures, expenditureLeftOutReason, nameOf)} />
    </Stack>
  );
}

export function LifeEventSectionTotal<T extends LifeEvent>({
  lifeEvents,
  currency,
  nameOf = lifeEventName,
}: {
  lifeEvents: T[];
  currency: TenantCurrency;
  nameOf?: (lifeEvent: T) => string;
}) {
  const total = lifeEvents.reduce((sum, item) => sum + (item.cost ?? 0), 0);

  return (
    <Stack gap={2} align="flex-end">
      <SectionTotal>-{formatMoney(total, currency)}</SectionTotal>
      <LeftOutNote names={leftOutNames(lifeEvents, lifeEventLeftOutReason, nameOf)} />
    </Stack>
  );
}

function SectionTotal({ children, tooltipLabel }: { children: ReactNode; tooltipLabel?: string }) {
  if (!tooltipLabel) {
    return (
      <Text size="lg" fw={FONT_WEIGHTS.bold} c="gray.10" style={{ whiteSpace: 'nowrap' }}>
        {children}
      </Text>
    );
  }

  return (
    <UnderlineTooltip label={tooltipLabel} size="lg" fw={FONT_WEIGHTS.bold}>
      {children}
    </UnderlineTooltip>
  );
}
